import { Users, Check } from 'lucide-react';
import type { UserProfileDto } from '../../../domain/contracts/api.contracts';
import { TierLevel } from '../../../domain/enums/TierLevel';
import { useTranslation } from '../../../application/hooks/useTranslation';

/** W04 — nominee tiles. L1 only: agents/executives (L2–L6) never see nominee data, regardless of payload. */
export function NomineeCard({ user }: { user: Pick<UserProfileDto, 'tier' | 'nominee'> }) {
  const { t } = useTranslation();
  if (user.tier !== TierLevel.L1) return null;
  const n = user.nominee;

  const statusPill = n ? (
    <span className="profile-status-pill profile-pill-indigo">
      <Check size={12} aria-hidden />
      {t('profile.pillRegistered')}
    </span>
  ) : (
    <span className="profile-status-pill profile-pill-neutral">
      {t('profile.pillNotSet')}
    </span>
  );

  return (
    <section className="profile-card">
      <div className="profile-card-header">
        <div className="profile-card-header-main">
          <div className="profile-card-icon-tile profile-icon-indigo">
            <Users size={18} aria-hidden />
          </div>
          <div className="profile-card-title-wrap">
            <h3 className="profile-card-title">{t('profile.nominee')}</h3>
            <p className="profile-card-subtitle">{t('profile.nomineeSubtitle')}</p>
          </div>
        </div>
        <div className="profile-card-header-badge">{statusPill}</div>
      </div>

      <div className="profile-card-body">
        {n ? (
          <dl className="profile-kv-grid kv" data-testid="nominee-card">
            <div className="profile-kv-item profile-kv-full">
              <dt className="profile-kv-label">{t('profile.nomineeName')}</dt>
              <dd className="profile-kv-value profile-kv-highlight">{n.fullName}</dd>
            </div>
            <div className="profile-kv-item">
              <dt className="profile-kv-label">{t('profile.relation')}</dt>
              <dd className="profile-kv-value">{n.relation || <span className="muted">{t('profile.notOnRecord')}</span>}</dd>
            </div>
            <div className="profile-kv-item">
              <dt className="profile-kv-label">{t('profile.nomineeNid')}</dt>
              <dd className="profile-kv-value tabular">{n.nidMasked || <span className="muted">{t('profile.notOnRecord')}</span>}</dd>
            </div>
          </dl>
        ) : (
          <div className="profile-empty-state">
            <p className="profile-empty-text muted" data-testid="nominee-empty">
              {t('profile.notOnRecord')}
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
